import type { Terminal, ILinkProvider, ILink } from '@xterm/xterm';

/**
 * Custom xterm.js link provider that detects URLs spanning multiple
 * wrapped lines.  The built-in WebLinksAddon only matches within a
 * single physical line, so long URLs (e.g. OAuth callbacks) that wrap
 * are never clickable.
 *
 * This provider reconstructs the full "logical line" by following
 * xterm's `isWrapped` flag, runs a URL regex over the joined text,
 * then maps each match back to multi-line buffer coordinates.
 */

const URL_RE = /https?:\/\/[^\s'"<>\[\]{}()|\\^`]+/g;

export function registerLinkProvider(terminal: Terminal): void {
  terminal.registerLinkProvider(new WrappedUrlLinkProvider(terminal));
}

class WrappedUrlLinkProvider implements ILinkProvider {
  constructor(private terminal: Terminal) {}

  provideLinks(lineNumber: number, callback: (links: ILink[] | undefined) => void): void {
    const buffer = this.terminal.buffer.active;
    const cols = this.terminal.cols;

    // Walk backward to find the first physical line of the logical line.
    let startLine = lineNumber - 1; // 0-based
    while (startLine > 0 && buffer.getLine(startLine)?.isWrapped) {
      startLine--;
    }

    // Walk forward to find the last physical line.
    let endLine = startLine;
    while (endLine + 1 < buffer.length && buffer.getLine(endLine + 1)?.isWrapped) {
      endLine++;
    }

    // Build full logical line text.
    // Wrapped (non-final) lines are always full-width; the final line is trimmed.
    let fullText = '';
    for (let i = startLine; i <= endLine; i++) {
      const line = buffer.getLine(i);
      if (!line) continue;
      fullText += i < endLine ? line.translateToString(false, 0, cols) : line.translateToString(true);
    }

    // Match URLs and convert offsets to buffer coordinates.
    const links: ILink[] = [];
    const queryLine = lineNumber - 1; // 0-based

    URL_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_RE.exec(fullText)) !== null) {
      const startOffset = match.index;
      const endOffset = startOffset + match[0].length - 1;

      const sLine = startLine + Math.floor(startOffset / cols);
      const sCol = (startOffset % cols) + 1; // 1-based
      const eLine = startLine + Math.floor(endOffset / cols);
      const eCol = (endOffset % cols) + 1;

      // Only return links that overlap the queried line.
      if (sLine > queryLine || eLine < queryLine) continue;

      links.push({
        range: {
          start: { x: sCol, y: sLine + 1 },
          end: { x: eCol, y: eLine + 1 },
        },
        text: match[0],
        activate: (_event, text) => {
          window.open(text, '_blank', 'noopener,noreferrer');
        },
      });
    }

    callback(links.length > 0 ? links : undefined);
  }
}
